import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAdminAuth } from "../../context/AdminAuthContext";

export default function CategoriesPage() {
  const { adminAxios } = useAdminAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name_en: "", name_it: "" });
  const [saving, setSaving] = useState(false);

  const load = () => adminAxios.get("/admin/categories").then(({ data }) => setCategories(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name_en: "", name_it: "" }); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name_en: c.name_en, name_it: c.name_it || "" }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await adminAxios.put(`/admin/categories/${editing.id}`, form);
      else await adminAxios.post("/admin/categories", form);
      setShowForm(false);
      load();
    } finally { setSaving(false); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    await adminAxios.delete(`/admin/categories/${id}`);
    load();
  };

  return (
    <div className="p-8 min-h-screen bg-zinc-50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Categories</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Property categories — manage investment types</p>
        </div>
        <button onClick={openCreate} data-testid="add-category-btn" className="flex items-center gap-2 bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-5 py-2.5 transition-colors">
          <Plus size={14} strokeWidth={2} /> Add Category
        </button>
      </div>

      {/* Suggested defaults banner */}
      {categories.length === 0 && !loading && (
        <div className="bg-zinc-100 border border-zinc-200 p-5 mb-6 text-sm text-zinc-600">
          <p className="font-medium mb-2">Suggested categories to get started:</p>
          <div className="flex flex-wrap gap-2">
            {["Prime Residential", "Hospitality", "Commercial", "Historic Estates", "Land Development"].map(name => (
              <button key={name} onClick={async () => { await adminAxios.post("/admin/categories", { name_en: name, name_it: "" }); load(); }} className="text-xs bg-white border border-zinc-200 px-3 py-1.5 hover:border-zinc-400 transition-colors">
                + {name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              {["Name (EN)", "Name (IT)", "Created", ""].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={4} className="px-4 py-6 text-zinc-400">Loading...</td></tr>
            : categories.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-zinc-400 text-center">No categories yet.</td></tr>
            : categories.map(c => (
              <tr key={c.id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors" data-testid={`category-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-zinc-900">{c.name_en}</td>
                <td className="px-4 py-3 text-zinc-500">{c.name_it || "—"}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`} className="text-zinc-400 hover:text-zinc-700 transition-colors"><Edit2 size={13} strokeWidth={1.5} /></button>
                    <button onClick={() => del(c.id, c.name_en)} data-testid={`delete-cat-${c.id}`} className="text-zinc-400 hover:text-[#C8102E] transition-colors"><Trash2 size={13} strokeWidth={1.5} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white max-w-sm p-7">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900">{editing ? "Edit Category" : "Add Category"}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-400 mt-1">Categories help organize investment opportunities by type.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Name (English) *</label>
              <input type="text" value={form.name_en} onChange={e => setForm(f => ({...f, name_en: e.target.value}))} required className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400" placeholder="Prime Residential" data-testid="category-name-en" />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Name (Italian)</label>
              <input type="text" value={form.name_it} onChange={e => setForm(f => ({...f, name_it: e.target.value}))} className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400" placeholder="Residenziale Prime" data-testid="category-name-it" />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={saving} data-testid="save-category-btn" className="bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-6 py-2.5 transition-colors disabled:opacity-50">
                {saving ? "Saving..." : "Save"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-zinc-200 text-zinc-600 text-xs uppercase px-6 py-2.5 hover:bg-zinc-50">
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
